import { Box, Button, Card, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { AdPlatform } from '@marketing-tool/shared';
import type { Cohort } from '@marketing-tool/shared';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router';

import { PageWide } from '../../components/PageLayout';
import type { AdGroup, Campaign } from '../../data/game-report/types';
import type { Game } from '../../data/games';
import { AdInterpolator } from '../../platforms/line/components/AdInterpolator';
import { CampaignIntegrityCheck } from '../../platforms/line/components/CampaignIntegrityCheck';

import { AdGroupSelector } from './AdGroupSelector';
import { AdTable } from './AdTable';
import { CampaignSelector } from './CampaignSelector';
import { CohortSelector } from './CohortSelector';
import { PlatformSelector } from './PlatformSelector';

export const ReportsTab = () => {
    const { game, gameId } = useOutletContext<{ game: Game; gameId: string }>();
    const [platform, setPlatform] = useState<AdPlatform | undefined>(game?.platforms[0]);
    const [campaign, setCampaign] = useState<Campaign | undefined>();
    const [adGroup, setAdGroup] = useState<AdGroup | undefined>();
    const [cohort, setCohort] = useState<Cohort | undefined>();
    const [from, setFrom] = useState(dayjs().subtract(6, 'days'));
    const [to, setTo] = useState(dayjs());
    const [showReport, setShowReport] = useState(false);

    // Store the applied filter values
    const [appliedFilters, setAppliedFilters] = useState<{
        platform: AdPlatform;
        campaign: Campaign;
        adGroup: AdGroup;
        cohort: Cohort | undefined;
        from: dayjs.Dayjs;
        to: dayjs.Dayjs;
    } | null>(null);

    // Clear selections when upstream selections change
    useEffect(() => setPlatform(game?.platforms[0]), [game]);
    useEffect(() => setCampaign(undefined), [game, platform]);
    useEffect(() => setAdGroup(undefined), [campaign]);

    const isFormComplete = platform && campaign && adGroup;

    const handleApplyFilters = () => {
        if (isFormComplete) {
            setAppliedFilters({
                platform,
                campaign,
                adGroup,
                cohort,
                from,
                to,
            });
            setShowReport(true);
        }
    };

    return (
        <PageWide>
            <Stack gap="lg">
                <Card withBorder>
                    <Card.Section
                        inheritPadding
                        py="md"
                        bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))"
                    >
                        <Stack gap="md">
                            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                                <PlatformSelector
                                    id="platform"
                                    gameId={gameId}
                                    value={platform}
                                    onChange={setPlatform}
                                    label="Platform"
                                />

                                {platform && (
                                    <Box>
                                        <CampaignSelector
                                            id="campaign"
                                            gameId={gameId}
                                            platform={platform}
                                            value={campaign?.id}
                                            onChange={setCampaign}
                                            label="Campaign"
                                        />
                                        {platform === AdPlatform.Line && campaign && (
                                            <Box mt={8}>
                                                <AdInterpolator
                                                    gameId={gameId}
                                                    platform={platform}
                                                    campaignId={campaign.id}
                                                >
                                                    Interpolate campaign ads
                                                </AdInterpolator>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {platform && campaign && (
                                    <Box>
                                        <AdGroupSelector
                                            id="ad-group"
                                            gameId={gameId}
                                            platform={platform}
                                            campaignId={campaign.id}
                                            value={adGroup?.id}
                                            onChange={setAdGroup}
                                            label="Ad Group"
                                        />
                                        {platform === AdPlatform.Line && adGroup && (
                                            <Box mt={8}>
                                                <AdInterpolator
                                                    gameId={gameId}
                                                    platform={platform}
                                                    campaignId={campaign.id}
                                                    adGroupId={adGroup.id}
                                                >
                                                    Interpolate ad group ads
                                                </AdInterpolator>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {platform && (
                                    <CohortSelector
                                        id="cohort"
                                        gameId={gameId}
                                        value={cohort?.id}
                                        onChange={setCohort}
                                        label="Cohort (Optional)"
                                        clearable
                                    />
                                )}

                                <DatePickerInput
                                    type="range"
                                    label="Date Range"
                                    value={[from ? from.toDate() : null, to ? to.toDate() : null]}
                                    onChange={([start, end]) => {
                                        if (start) setFrom(dayjs(start));
                                        if (end) setTo(dayjs(end));
                                    }}
                                />
                            </SimpleGrid>

                            <Group justify="flex-end">
                                <Button onClick={handleApplyFilters} disabled={!isFormComplete}>
                                    Apply Filters
                                </Button>
                            </Group>
                        </Stack>
                    </Card.Section>
                </Card>

                {campaign && platform && (
                    <Card withBorder p="md">
                        <CampaignIntegrityCheck gameId={gameId} platform={platform} campaignId={campaign.id} />
                    </Card>
                )}

                {showReport && appliedFilters && (
                    <Card withBorder p="md">
                        <Stack gap="md">
                            <Text fw={600}>
                                {appliedFilters.adGroup.name} — {appliedFilters.from.format('MMM D')} to{' '}
                                {appliedFilters.to.format('MMM D, YYYY')}
                                {appliedFilters.cohort && ` (${appliedFilters.cohort.name})`}
                            </Text>
                            <AdTable
                                gameId={gameId}
                                platform={appliedFilters.platform}
                                campaignId={appliedFilters.campaign.id}
                                adGroupId={appliedFilters.adGroup.id}
                                from={appliedFilters.from}
                                to={appliedFilters.to}
                                cohortId={appliedFilters.cohort?.id}
                            />
                        </Stack>
                    </Card>
                )}
            </Stack>
        </PageWide>
    );
};
